/**
 * Drizzle mutations (PowerSync local-first) for the WORKFORCE domain.
 *
 * GOTCHA #15: PowerSync tables are SQLite VIEWS → NEVER perform an UPSERT
 * (`onConflictDoUpdate` does not exist). Every write is SELECT→(UPDATE|INSERT):
 * read the row by id before deciding. Create = INSERT; edit/review =
 * UPDATE by id; delete profile = DELETE (the server write path materializes
 * it as a SOFT-DELETE; the supervisor's audit log survives).
 *
 * The server's JSONB columns (disclosure/consent/work_schedule/
 * productivity_catalog/attribution_rules/productivity/detected_apps) are stored
 * as TEXT containing serialized JSON; the connector rehydrates them on upload.
 * `user_id` + `organization_id` travel DENORMALIZED (required by sync rules).
 *
 * HARD INVARIANT (§2e): NEVER self-bill. Approving a timesheet requires an
 * assigned project; without one, it does NOT move to 'approved' (see
 * `statusAfterApprove`).
 *
 * ── §4.11 `infra/PENDING_WORK.md`: why reviews go through
 *    `patchRowVerified`/`waitForRowPresent` ────────────────────────────────
 * `employee_profile` and `proposed_time_entry` are the BASE of payroll: a Drizzle
 * `UPDATE … WHERE id = ?` against the PowerSync view can match ZERO rows and
 * return "success" if the local checkpoint does not yet contain the row
 * (the reconstruction window after the CRUD queue is cleared) — the review,
 * project assignment, or employee approval would disappear silently.
 * `approveProposedEntryMutation` also READS before deciding (invariant §2e):
 * without waiting for the row to be present, that read could confuse
 * "not synchronized yet" with "no project assigned" and return a false
 * rejection — `waitForRowPresent` comes first to distinguish the two cases
 * before deciding.
 *
 * ── Organization scope: verified, NOT applicable today ─────────────────────
 * `infra/sync_streams.yaml` (`org_workforce`) synchronizes `employee_profile` +
 * `proposed_time_entry` from ALL organizations the user belongs to at once
 * (`organization_id IN my_org_ids`, not just the active one) — the same pattern
 * that motivated `options.scope` in `comandero-app`. We explicitly checked
 * whether it is needed here and the answer is no, today: (1) the `id` values
 * are UUIDs generated with `generateId()`, globally unique — there cannot be
 * a collision between rows from two different organizations under the same
 * `id`, unlike `comandero`'s `orderId`; (2) none of the functions below
 * already had an `organization_id` condition in their `WHERE` that this change
 * could discard (verified; there is nothing to preserve); (3) this app has no
 * concept of an "active organization" —
 * `getEmployeeProfilesQuery`/`getProposedEntriesByProfileQuery` deliberately
 * return EVERYTHING the sync rules already authorized, mixing personnel +
 * all orgs, so there is no expected `organization_id` value against which to
 * bound `scope`. The signal that would reopen this: the app adding an active-
 * organization selector for this domain — at that point, `options.scope`
 * (already in the canon) is the piece to use.
 */

import { eq } from "drizzle-orm";
import { employee_profile, type PowerSyncDrizzleDB, proposed_time_entry } from "@jornada/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import {
  type ConsentRecord,
  type Disclosure,
  type ProductivityCatalog,
  type ProjectRef,
  type ProjectRule,
  statusAfterApprove,
  statusAfterEdit,
  statusAfterReject,
  type WorkSchedule,
} from "./types";

function toJson(value: unknown): string | null {
  return value === null || value === undefined ? null : JSON.stringify(value);
}

// ─── Employee profiles (creation / configuration editing / removal) ───────────

export interface CreateEmployeeProfileInput {
  userId: string;
  organizationId?: string | null;
  name: string;
  deviceLabel?: string | null;
  legalBasis?: string | null;
  disclosure?: Disclosure | null;
  consent?: ConsentRecord | null;
  workSchedule?: WorkSchedule | null;
  productivityCatalog?: ProductivityCatalog | null;
  attributionRules?: ProjectRule[] | null;
}

export async function createEmployeeProfileMutation(
  db: PowerSyncDrizzleDB,
  input: CreateEmployeeProfileInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(employee_profile).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    name: input.name,
    device_label: input.deviceLabel ?? null,
    legal_basis: input.legalBasis ?? input.disclosure?.basis ?? "employee_consent",
    disclosure: toJson(input.disclosure ?? null),
    consent: toJson(input.consent ?? null),
    work_schedule: toJson(input.workSchedule ?? null),
    productivity_catalog: toJson(input.productivityCatalog ?? null),
    attribution_rules: toJson(input.attributionRules ?? null),
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface UpdateEmployeeProfileInput {
  name?: string;
  deviceLabel?: string | null;
  disclosure?: Disclosure;
  consent?: ConsentRecord | null;
  workSchedule?: WorkSchedule | null;
  productivityCatalog?: ProductivityCatalog | null;
  attributionRules?: ProjectRule[] | null;
  archived?: number;
}

export async function updateEmployeeProfileMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  input: UpdateEmployeeProfileInput,
  options?: VerifiedWriteOptions,
): Promise<void> {
  // `updated_at` is NOT set here: `patchRowVerified` stamps it (and ignores
  // any value arriving in the patch) on every attempt.
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) {
    patch.name = input.name;
  }
  if (input.deviceLabel !== undefined) {
    patch.device_label = input.deviceLabel;
  }
  if (input.disclosure !== undefined) {
    patch.disclosure = toJson(input.disclosure);
    patch.legal_basis = input.disclosure.basis;
  }
  if (input.consent !== undefined) {
    patch.consent = toJson(input.consent);
  }
  if (input.workSchedule !== undefined) {
    patch.work_schedule = toJson(input.workSchedule);
  }
  if (input.productivityCatalog !== undefined) {
    patch.productivity_catalog = toJson(input.productivityCatalog);
  }
  if (input.attributionRules !== undefined) {
    patch.attribution_rules = toJson(input.attributionRules);
  }
  if (input.archived !== undefined) {
    patch.archived = input.archived;
  }
  await patchRowVerified(db, employee_profile, employee_profile.id, id, patch, options);
}

/**
 * Profile removal: local DELETE (the write path converts it to a server-side
 * soft-delete; the supervisor's audit log is preserved).
 */
export async function deleteEmployeeProfileMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(employee_profile).where(eq(employee_profile.id, id));
}

// ─── Review of proposed timesheets (GOTCHA #15: read-before-write) ───────────
//
// Timesheets ALREADY exist (the server bridge generates them). Review = UPDATE
// by id, never upsert. `approve` reads the row (SELECT) to respect the invariant:
// it is not marked 'approved' without an assigned project.

/** The employee assigns/changes the project. Returns to 'edited' (it must be re-approved). */
export async function assignProjectMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  project: ProjectRef,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    proposed_time_entry,
    proposed_time_entry.id,
    id,
    {
      project_id: project.id,
      project_code: project.code,
      project_name: project.name,
      status: statusAfterEdit(),
    },
    options,
  );
}

/** The employee adjusts the billable duration. Returns to 'edited'. */
export async function editDurationMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  durationSecs: number,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    proposed_time_entry,
    proposed_time_entry.id,
    id,
    { duration_secs: durationSecs, status: statusAfterEdit() },
    options,
  );
}

/** The employee edits the description. Returns to 'edited'. */
export async function editDescriptionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  description: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    proposed_time_entry,
    proposed_time_entry.id,
    id,
    { description, status: statusAfterEdit() },
    options,
  );
}

/**
 * The employee APPROVES the timesheet. It reads the row first (SELECT-then-UPDATE):
 * it moves to 'approved' only if it has a project; otherwise the state is preserved
 * and `false` is returned (time cannot be billed without a project — hard invariant).
 *
 * `waitForRowPresent` goes BEFORE the deciding SELECT, not just before the UPDATE:
 * without it, a timesheet just generated by the server bridge that has not yet
 * reached the local checkpoint reads as ABSENT, and this function returned `false` —
 * the same result as "no project assigned" (see the caller's toast in
 * `review-view.tsx`), a false negative that confuses a sync window with a
 * business invariant. Waiting happens outside any transaction (there is none
 * in this module; see `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §6.0).
 */
export async function approveProposedEntryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<boolean> {
  await waitForRowPresent(db, proposed_time_entry, proposed_time_entry.id, id, options);
  const [row] = await db
    .select({ status: proposed_time_entry.status, project_id: proposed_time_entry.project_id })
    .from(proposed_time_entry)
    .where(eq(proposed_time_entry.id, id));
  if (!row) {
    return false;
  }
  const hasProject = Boolean(row.project_id);
  const nextStatus = statusAfterApprove(row.status, hasProject);
  await patchRowVerified(
    db,
    proposed_time_entry,
    proposed_time_entry.id,
    id,
    { status: nextStatus },
    options,
  );
  return hasProject;
}

/** The employee discards the timesheet. It moves to 'rejected' (never billable). */
export async function rejectProposedEntryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    proposed_time_entry,
    proposed_time_entry.id,
    id,
    { status: statusAfterReject() },
    options,
  );
}
